// Import React and Material UI components
import React from "react";
import {
  Container,
  Grid,
  Typography,
  Menu,
  MenuItem,
  Button,
  CircularProgress,
} from "@material-ui/core";
import {
  makeStyles,
  MuiThemeProvider,
  createMuiTheme,
} from "@material-ui/core/styles";
import { Skeleton } from "@material-ui/lab";
import MUIDataTable from "mui-datatables";
import { useHistory } from "react-router";
import PropTypes from "prop-types";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { startOfDay, endOfDay } from "date-fns";

// Import API Services
import { getOrders, sendOrdersToAcumatica } from "../api/OrderInvoices";

// Import custom components
import SnackbarMessage from "../components/SnackbarMessage";

/**
 * Styles for the component
 */
const useStyles = makeStyles((theme) => ({
  mainContainer: {
    [theme.breakpoints.up("lg")]: {
      paddingLeft: "190px",
    },
    [theme.breakpoints.down("xl")]: {
      marginTop: "85px",
    },
    [theme.breakpoints.between("sm", "md")]: {
      marginTop: "75px",
    },
    [theme.breakpoints.up("md")]: {
      marginTop: "65px",
    },
  },
  ListItem: {
    whiteSpace: "nowrap",
    overflow: "visible",
    textOverflow: "ellipsis",
  },
}));

/**
 * Orders and Invoices Table List Page
 * @returns {JSX.Element}
 */
const OrdersInvoicesTableList = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState({
    message: "",
    severity: "info",
    open: false,
  });
  const [contextMenu, setContextMenu] = React.useState(null);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [columnViewPercentage, setColumnViewPercentage] = React.useState(
    100 / 7,
  );
  const [role, setRole] = React.useState(null);
  const [sending, setSending] = React.useState(false);
  const [clientSelected, setClientSelected] = React.useState(null);
  const [synced, setSynced] = React.useState(false);
  const [acumatica_enabled, setAcumaticaEnabled] = React.useState(false);
  const [countRows, setCountRows] = React.useState(0);

  /**
   * First render effect to fetch orders
   */
  React.useEffect(() => {
    // Get user role from local storage
    const role = localStorage.getItem("role");
    setRole(role);
    // Load orders from API
    getOrdersInvoices();
  }, []);

  /**
   * Close snackbar
   */
  const handleClose = () => {
    setError({
      open: false,
      message: "",
      severity: "info",
    });
    setContextMenu(null);
    setSelectedRow(null);
  };

  /**
   * Fetch orders from API based on user role and reseller ID
   */
  const getOrdersInvoices = () => {
    // Force loading state to true
    setLoading(true);
    // Get user role from local storage
    const role = localStorage.getItem("role");
    // Get reseller ID from local storage
    const resellerId = localStorage.getItem("reseller_id");
    // Initialize query parameters
    let queryParams = {};
    // If user is reseller admin, add reseller ID to query parameters
    if (role == "reselleradmin" || role == "superadmin") {
      queryParams.reseller_id = resellerId;
    }
    // Validate if user is connected as client admin, order approver, product manager or user manager
    if (
      role == "clientadmin" ||
      role == "clientorderapprover" ||
      role == "clientproductmanager" ||
      role == "clientusermanager"
    ) {
      // Add client ID to query parameters
      const clientId = localStorage.getItem("client_id");
      queryParams.client_id = clientId;
    }
    getOrders(queryParams)
      .then((response) => {
        // Check if response is valid and contains orders data
        if (
          response &&
          response.data &&
          response.data.getOrdersData &&
          Array.isArray(response.data.getOrdersData)
        ) {
          // Set orders state with fetched data
          setOrders(response.data.getOrdersData);
        } else {
          let error_message = response.errors
            ? response.errors[0].message
            : "An error occurred";
          // Display error
          setError({
            open: true,
            message: `${error_message}. Please try again later.`,
            severity: "error",
          });
        }
      })
      .catch(() => {
        // Display error
        setError({
          open: true,
          message: `An error occurred while fetching orders. Please try again later.`,
          severity: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /**
   * Function to handle right click on table row
   * @param {MouseEvent} event - The mouse event object
   */
  const handleContextMenu = (event) => {
    // Validate event and its properties before accessing closest
    if (!event || !event.target || typeof event.target.closest !== "function") {
      return;
    }
    // Search the closest td element
    const td = event.target.closest("td");
    // If the td is found, get the input checkbox
    if (td) {
      // Search for the checkbox input inside the td (it may be nested within several div/span)
      const checkbox = td.querySelector('input[type="checkbox"]');
      if (checkbox) {
        // If the checkbox was found, do not show the context menu
        return;
      }
    }
    // Find the closest table row
    const tr = event.target.closest("tr");
    // Find the first td in the row
    const tdd = tr ? tr.querySelector("td") : null;
    // Get the order invoice id from the row data
    const order_id = tdd
      ? tdd.querySelector("div div").textContent.trim()
      : null;
    // Check if the selected row is valid
    if (!order_id || isNaN(Number(order_id))) {
      return;
    }
    // Prevent the default context menu from appearing
    event.preventDefault();
    if (event.ctrlKey || event.metaKey || event.type === "contextmenu") {
      // Set the selected row and context menu position
      setSelectedRow(order_id);
      // Set the context menu position
      setContextMenu({
        mouseX: event.clientX - 2,
        mouseY: event.clientY - 4,
      });
    } else if (event.type === "click") {
      history.push(`/app/orders/${order_id}`);
    }
  };

  /**
   * Open new tab with order details or copy link to clipboard
   * @param {boolean} openNewTab - If true, open in new tab, else copy link to clipboard
   * @param {*} openNewTab
   */
  const handleOpenInNewTab = (openNewTab = true) => {
    // Check if selectedRow is valid
    if (selectedRow) {
      // Get the order ID from the selected row
      const order_id = selectedRow;
      // Open the new tab with the order details page
      const url = `/app/orders/${order_id}`;
      // Check if openNewTab is true
      if (openNewTab) {
        // Open the URL in a new tab
        window.open(url, "_blank");
      } else {
        // Copy the URL to the clipboard
        navigator.clipboard
          .writeText(`${process.env.REACT_APP_WEB}${url}`)
          .then(() => {
            setError({
              open: true,
              message: `Link copied to clipboard`,
              severity: "success",
            });
          });
      }
    }
    handleClose();
  };

  /**
   * Adjust the table style based on the number of columns
   * @returns {Array} initial_columns - The initial columns for the orders table
   */
  const theme = () =>
    createMuiTheme({
      overrides: {
        // Table head custom styles
        MUIDataTableHeadCell: {
          fixedHeader: {
            paddingTop: "0px",
            paddingBottom: "10px",
            backgroundColor: "#F2F2F2",
            borderRight: "1px solid #E5E3E3",
            overflow: "visible",
          },
        },
        // Search form custom style
        MUIDataTableSearch: {
          main: {
            visibility: true,
          },
        },
        // Chip filters custom styles
        MUIDataTableFilterList: {
          root: {
            marginBottom: "10px",
          },
        },
        // Filter container custom styles
        MuiGrid: {
          container: {
            display: "block",
            width: "210px",
            paddingLeft: "opx",
          },
          item: {
            boxSizing: "content-box",
          },
        },
        // Filter list options custom styles
        MUIDataTableFilter: {
          checkboxIcon: {
            padding: "0px",
          },
          checkboxFormControlLabel: {
            padding: "0px",
          },
          checkboxListTitle: {
            width: "100%",
          },
          gridListTile: {
            maxWidth: "100%",
          },
        },
        MUIDataTableSelectCell: {
          headerCell: {
            backgroundColor: "#F2F2F2",
          },
        },
        MUIDataTableToolbarSelect: {
          root: { visibility: "hidden", minHeight: "49px", display: "none" },
        },
        MuiTableHead: {
          root: {
            display: `${columnViewPercentage ? "table-header-group" : "none"}`,
            overflow: "visible",
          },
        },
        MuiTableBody: {
          root: {
            display: `${columnViewPercentage ? "table-row-group" : "none"}`,
          },
        },
      },
    });

  /**
   * Render custom style for table column header
   * @param {*} param0
   * @returns
   */
  const customStyle = ({ ...column }) => {
    return (
      <div
        className={classes.ListItem}
        style={{
          maxWidth: "100%",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
          fontWeight: "bold",
          padding: "10px",
          height: "10px",
        }}
      >
        {column.label}
      </div>
    );
  };

  /**
   * Format date to readable string
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // Format date as MM/DD/YYYY
    const newDate = `${
      date.getMonth() + 1
    }/${date.getDate()}/${date.getFullYear()}`;
    // Format time as hh:mm AM/PM
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${newDate}, ${hours}:${minutes} ${ampm}`;
  };

  /**
   * Render custom style for table row header
   * @param {*} value
   * @returns
   */
  const customStyleText = (value = "-", isDate = false) => {
    // Format date if isDate is true
    if (isDate) {
      value = formatDate(value);
    }
    return (
      <div
        style={{
          maxWidth: "300px",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
        }}
      >
        {value}
      </div>
    );
  };

  /**
   * Options for the MUIDataTable component
   */
  let initial_columns = [
    {
      name: "id",
      label: "Order Invoice Number",
      sort: false,
      options: {
        filter: false,
        setCellProps: () => {
          return {
            style: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          };
        },
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleText(value),
      },
    },
    {
      name: "customer_name",
      label: "Customer",
      options: {
        filter: false,
        setCellProps: () => {
          return {
            style: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          };
        },
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleText(value),
      },
    },
    {
      name: "customer_email",
      label: "Customer Email",
      options: {
        filter: false,
        setCellProps: () => {
          return {
            style: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          };
        },
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleText(value),
      },
    },
    {
      name: "ordered_by",
      label: "Ordered By",
      options: {
        filter: false,
        setCellProps: () => {
          return {
            style: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          };
        },
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleText(value),
      },
    },
    {
      name: "ordered_email",
      label: "Ordered By Email",
      options: {
        filter: false,
        setCellProps: () => {
          return {
            style: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          };
        },
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleText(value),
      },
    },
    // If the user is reselleradmin or superadmin
    ...(role === "reselleradmin" || role === "superadmin"
      ? [
          // Show the client_name column with filter
          {
            name: "client_name",
            label: "Filter by client",
            options: {
              filter: true,
              display: "excluded",
              filterType: "dropdown",
              filterOptions: {
                names: props.clients
                  ? props.clients.map((client) => client.name)
                  : [],
              },
              setCellProps: () => {
                return {
                  style: {
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  },
                };
              },
              customHeadLabelRender: customStyle,
              customBodyRender: (value) => customStyleText(value),
            },
          },
          // Show the acumatica_sync column with filter
          {
            name: "acumatica_sync",
            label: "Filter by acumatica Sync",
            options: {
              filter: true,
              display: "excluded",
              filterType: "dropdown",
              filterOptions: {
                names: ["Not synced with Acumatica"],
              },
              setCellProps: () => {
                return {
                  style: {
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  },
                };
              },
              customHeadLabelRender: customStyle,
              customBodyRender: (value) => customStyleText(value),
            },
          },
        ]
      : []),
    {
      name: "Status",
      label: "Status",
      options: {
        filter: true,
        filterType: "dropdown",
        filterOptions: {
          names: [
            "Created",
            "SubmittedForApproval",
            "Denied",
            "Canceled",
            "Shipping",
            "BackOrder",
            "Shipped",
            "Completed",
          ],
        },
        setCellProps: () => {
          return {
            style: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          };
        },
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleText(value),
      },
    },
    {
      name: "created_at",
      label: "Created Date",
      options: {
        filter: false,
        setCellProps: () => ({
          style: {
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
          },
        }),
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleText(value, true),
      },
    },
    {
      name: "create_date",
      label: "Filter by Created Date",
      options: {
        filter: true,
        display: "excluded",
        filterType: "custom",
        label: "Filter by Created Date",
        filterOptions: {
          logic: (create_date, filters) => {
            // If no filters are applied, do not filter any rows
            if (filters.length == 0) return false;
            //Get start and end date from filters
            const startDate = filters[0] ? new Date(filters[0]) : null;
            const endDate = filters[1] ? new Date(filters[1]) : null;
            const rowDate = new Date(create_date);
            // If both start and end dates are set, filter rows between the two dates (inclusive)
            if (startDate && endDate) {
              return rowDate < startDate || rowDate > endDate;
            } else if (startDate) {
              // If only start date is set, filter rows after the start date
              return rowDate < startDate;
            } else if (endDate) {
              // If only end date is set, filter rows before the end date
              return rowDate > endDate;
            }
          },
          display: (() => {
            const DateRangeFilter = (filterList, onChange, index, column) => (
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0px",
                    padding: "0px",
                  }}
                >
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.54)",
                      padding: 0,
                      fontSize: "12px",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                      fontWeight: 400,
                      lineHeight: 1,
                      letterSpacing: "0.00938em",
                    }}
                  >
                    Filter by Created Date
                  </span>
                  <DatePicker
                    autoOk
                    style={{ width: "100%" }}
                    label="Start Date"
                    format="MM/dd/yyyy"
                    clearable
                    disableFuture
                    maxDate={filterList[index][1] || new Date("2100-01-01")}
                    value={filterList[index][0] || null}
                    onChange={(date) => {
                      // Get the start of the day for the selected date
                      const start = date ? startOfDay(date) : null;
                      let formattedStart = null;
                      // Check if start date is valid before formatting
                      if (start) {
                        // Format the start date as MM/dd/yyyy
                        formattedStart = `${(start.getMonth() + 1)
                          .toString()
                          .padStart(2, "0")}/${start
                          .getDate()
                          .toString()
                          .padStart(2, "0")}/${start.getFullYear()}`;
                      }
                      // Update the filter list with the formatted date
                      filterList[index][0] = formattedStart
                        ? formattedStart
                        : null;
                      // Set the filter value
                      onChange(filterList[index], index, column);
                    }}
                    disableenforcefocus={"true"}
                  />
                  <DatePicker
                    autoOk
                    style={{ width: "100%" }}
                    label="End Date"
                    format="MM/dd/yyyy"
                    clearable
                    disableFuture
                    minDate={filterList[index][0] || new Date("1900-01-01")}
                    value={filterList[index][1] || null}
                    onChange={(date) => {
                      const end = date ? endOfDay(date) : null;
                      let formattedEnd = null;
                      // Check if end date is valid before formatting
                      if (end) {
                        // Format the end date as MM/dd/yyyy
                        formattedEnd = `${(end.getMonth() + 1)
                          .toString()
                          .padStart(2, "0")}/${end
                          .getDate()
                          .toString()
                          .padStart(2, "0")}/${end.getFullYear()}`;
                      }
                      // Update the filter list with the formatted date
                      filterList[index][1] = formattedEnd ? formattedEnd : null;
                      onChange(filterList[index], index, column);
                    }}
                    disableenforcefocus={"true"}
                  />
                </div>
              </MuiPickersUtilsProvider>
            );
            DateRangeFilter.displayName = "DateRangeFilter";
            return DateRangeFilter;
          })(),
        },
        // Chip view options
        customFilterListOptions: {
          // Chip label rendering
          render: (v) => {
            // Get start and end dates from filter values
            const start = v[0] ? new Date(v[0]) : null;
            const end = v[1] ? new Date(v[1]) : null;
            if (!start && !end) return [];
            // If both start and end dates are set, format them as MM/dd/yyyy
            // Initialize the chip label
            return `Range: ${start ? start.toLocaleDateString() : "..."} – ${
              end ? end.toLocaleDateString() : "..."
            }`;
          },
          // Delete chip action
          update: (filterList, filterPos, index) => {
            filterList[index] = []; // Force clear both start and end dates
            return filterList;
          },
        },
        setCellProps: () => ({
          style: {
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
          },
        }),
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleText(value, true),
      },
    },
  ];

  //Table options
  const options = {
    filter: true,
    filterType: "checkbox",
    sort: false,
    sortFilterList: false,
    selectableRows: "none",
    searchPlaceholder: "Search available orders",
    textLabels: {
      body: {
        noMatch: "Sorry, no matching records found",
        toolTip: "Sort",
        columnHeaderTooltip: (column) => `${column.label}`,
      },
    },
    downloadOptions: {
      filename: "Product List",
      separator: ",",
      filterOptions: {
        useDisplayedRowsOnly: true,
      },
    },
    responsive: "standard",
    elevation: 0,
    onTableChange: (action, state) => {
      // Set the column view percentage when columns are changed
      // columns porcetage views
      if (action == "viewColumnsChange") {
        const columns = state.columns;
        let countColumns = columns.length;
        let showColumns = 0;
        for (const column of columns) {
          if (column.display == "true") {
            showColumns++;
          }
        }
        if (showColumns !== columns.length) {
          countColumns = showColumns;
        }
        const columnViewPercentage =
          countColumns > 0 ? 100 / countColumns : null;
        setColumnViewPercentage(columnViewPercentage);
      }
      // Set the count of rows when filter is applied
      if (action === "filterChange") {
        const countRows = state.displayData.length;
        setCountRows(countRows);
      }
    },
    rowsPerPage: 10,
    rowsPerPageOptions: [10, 20, 50, 100],
    onFilterChange: (column, filterList) => {
      // If the client_name column is filtered, set the clientSelected state
      if (column === "client_name") {
        // Find the client by name
        const client = props.clients.find(
          (c) =>
            c.name ===
            filterList[
              initial_columns.findIndex((col) => col.name === "client_name")
            ][0],
        );
        // Set the clientSelected state to the client ID or null if not found
        setClientSelected(client ? client.id : null);
        setAcumaticaEnabled(
          client && client.acumatica ? client.acumatica : false,
        );
      }
      // If the acumatica_sync column is filtered, set the synced state
      if (column === "acumatica_sync") {
        const acumatica_sync =
          filterList[
            initial_columns.findIndex((col) => col.name === "acumatica_sync")
          ][0];
        setSynced(acumatica_sync);
      }
    },
    //  Custom CSV download to include only visible columns
    onDownload: (buildHead, buildBody, columns, data) => {
      // Filter out columns that are not displayed
      const filteredColumns = columns.filter((col) => col.display === "true");
      // Filter data to include only visible columns
      data = data.map((row) => ({
        ...row,
        data: row.data.filter(
          (cell, index) => columns[index].display === "true",
        ),
      }));
      // Build CSV with only visible columns
      let csv = buildHead(filteredColumns) + buildBody(data);
      return csv;
    },
  };

  /**
   * Function to sync orders with Acumatica
   */
  const syncOrdersWithAcumatica = () => {
    setSending(true);
    let params = {
      client_id: clientSelected,
    };
    sendOrdersToAcumatica(params)
      .then((response) => {
        // Check response data
        const response_data = response?.data?.sendOrdersToAcumatica ?? [];
        // If there are synced orders, show success message
        if (response_data.length > 0) {
          setError({
            open: true,
            message: "Orders successfully synced",
            severity: "success",
          });
          // Reload orders
          getOrdersInvoices();
          // Clear client selection and synced state
          setClientSelected(null);
          setSynced(false);
        }
        // If no orders were synced, show info message
        else {
          let error_message = response.errors
            ? response.errors[0].message
            : "An error occurred";
          let message = "";
          // Determine if the error is a JSON type
          if (
            typeof error_message === "string" &&
            error_message.includes("[{")
          ) {
            const errors = JSON.parse(error_message);
            let errors_message = "";
            for (const error of errors) {
              errors_message += "\r\n" + error.error;
            }
            message = `${errors_message}, Please contact support.`;
          } else {
            message = `${error_message}. Please try again later.`;
          }
          // Display error
          setError({
            open: true,
            message: message,
            severity: "error",
          });
        }
      })
      .catch(() => {
        // Display error
        setError({
          open: true,
          message: `An error occurred`,
          severity: "error",
        });
      })
      .finally(() => setSending(false));
  };

  /**
   * Render Orders and Invoices Table List Page
   * Note: The actual table component to display orders is not implemented in this snippet.
   */
  return (
    <Container className={classes.mainContainer} maxWidth="xl">
      {/** Grid container for the main content */}
      <Grid container style={{ padding: "20px" }}>
        {/** Page title */}
        <Grid item xs={6}>
          <Typography variant="h4" gutterBottom>
            Order Invoice History
          </Typography>
        </Grid>
        {clientSelected && synced == "Not synced with Acumatica" && (
          <Grid item xs={6} style={{ textAlign: "right" }}>
            <div style={{ display: "inline-flex", alignItems: "center" }}>
              <Button
                variant="contained"
                color="primary"
                disabled={
                  countRows === 0 || loading || sending || !acumatica_enabled
                }
                onClick={() => syncOrdersWithAcumatica()}
              >
                Send to Acumatica
              </Button>
              {sending && (
                <CircularProgress size={24} style={{ marginLeft: 12 }} />
              )}
            </div>
          </Grid>
        )}
        {/** Skeleton loader for the table */}
        {loading ? (
          <React.Fragment>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={9}>
                    <Skeleton
                      variant="rectangular"
                      height={30}
                      style={{ margin: "3px" }}
                      width={120}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={3}
                    style={{ paddingRight: "25px", marginBottom: "20px" }}
                  >
                    {[1, 2, 3, 4, 5].map((col, j) => {
                      return (
                        <>
                          <Skeleton
                            variant="rectangular"
                            height={30}
                            style={{ margin: "3px", float: "right" }}
                            width={40}
                            key={j}
                          />
                        </>
                      );
                    })}
                  </Grid>
                </Grid>
                <Grid container>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((row, i) => {
                    return (
                      <Grid key={i} container>
                        {[1, 2, 3, 4, 5, 6].map((col, j) => {
                          return (
                            <Grid item xs={2} key={j}>
                              <Skeleton
                                variant="rectangular"
                                height={39}
                                style={{ margin: "3px", marginBottom: "15px" }}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            </Grid>
          </React.Fragment>
        ) : (
          <>
            {/**Table container */}
            <Grid container>
              {/**Table */}
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                className={classes.ListItem}
                style={{ marginTop: "15px" }}
              >
                <MuiThemeProvider theme={theme}>
                  <div
                    onContextMenu={handleContextMenu}
                    onClick={handleContextMenu}
                  >
                    <MUIDataTable
                      data={orders}
                      columns={initial_columns}
                      options={options}
                    />
                  </div>
                  {/**Menu - Open new tab */}
                  <Menu
                    open={contextMenu !== null}
                    onClose={handleClose}
                    anchorReference="anchorPosition"
                    anchorPosition={
                      contextMenu
                        ? {
                            top: contextMenu.mouseY,
                            left: contextMenu.mouseX,
                          }
                        : undefined
                    }
                    autoFocus={false}
                    disableAutoFocusItem
                    disableRestoreFocus
                  >
                    <MenuItem onClick={handleOpenInNewTab}>
                      Open link in a new tab
                    </MenuItem>
                    <MenuItem onClick={() => handleOpenInNewTab(false)}>
                      Copy link
                    </MenuItem>
                  </Menu>
                </MuiThemeProvider>
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
      {/**Show SnackbarMessage for errors */}
      <SnackbarMessage
        open={error.open}
        message={error.message}
        severity={error.severity}
        handleClose={handleClose}
      />
    </Container>
  );
};

OrdersInvoicesTableList.displayName = "OrdersInvoicesTableList";

OrdersInvoicesTableList.propTypes = {
  clients: PropTypes.array,
};

export default OrdersInvoicesTableList;
